import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { 
  Globe, 
  Code, 
  Search, 
  Zap,
  Award,
  CheckCircle,
  ArrowRight,
  Phone,
  Layout,
  Settings,
  TrendingUp
} from 'lucide-react';

const DigitalWebPage = () => {
  const servicesWordpress = [
    {
      title: "Création de site WordPress",
      description: "Sites vitrines, blogs professionnels, portfolios adaptés à votre image de marque."
    },
    {
      title: "Refonte et optimisation",
      description: "Modernisation de votre site existant pour améliorer performances et conversions."
    },
    {
      title: "E-commerce WooCommerce",
      description: "Boutiques en ligne complètes avec gestion des stocks et paiements sécurisés."
    },
    {
      title: "Maintenance et évolution",
      description: "Support technique, mises à jour et ajout de fonctionnalités."
    }
  ];

  const structuration = [
    {
      icon: Layout,
      title: "Architecture de l'information",
      description: "Organisation claire et intuitive de vos contenus pour une navigation optimale."
    },
    {
      icon: Settings,
      title: "Outils collaboratifs",
      description: "Mise en place de solutions pour faciliter le travail d'équipe (Notion, Slack, etc.)."
    },
    {
      icon: Zap,
      title: "Automatisation des flux",
      description: "Connexion de vos outils pour éliminer les tâches répétitives et gagner en efficacité."
    },
    {
      icon: TrendingUp,
      title: "Tableaux de bord",
      description: "Visualisation de vos indicateurs clés pour piloter votre activité."
    }
  ];

  const automatisation = [
    "Synchronisation automatique de vos données entre applications",
    "Envoi d'emails et notifications automatisés",
    "Génération de documents et rapports",
    "Gestion automatisée des leads et prospects",
    "Publication programmée sur les réseaux sociaux",
    "Facturation et relances automatiques"
  ];

  const seo = [
    {
      title: "Audit SEO complet",
      description: "Analyse technique, sémantique et concurrentielle de votre positionnement."
    },
    {
      title: "Optimisation on-page",
      description: "Amélioration de la structure, des contenus et des balises pour le référencement."
    },
    {
      title: "Stratégie de contenu",
      description: "Définition d'une ligne éditoriale alignée avec vos objectifs business."
    },
    {
      title: "Suivi et reporting",
      description: "Tableau de bord mensuel de vos performances et recommandations."
    }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#E9DFCF' }} data-testid="digital-web-page">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-blue-700 to-blue-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium mb-6" style={{ backgroundColor: '#D8C9B3', color: '#2C2A27' }}>
                <Award className="w-4 h-4" />
                Accompagnement sur mesure
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6" style={{ letterSpacing: '-0.04em' }}>
                Digital & Web – Votre empreinte numérique
              </h1>
              <p className="text-xl text-blue-100 mb-8">
                Création web, structuration digitale et visibilité.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/#formulaire">
                  <Button 
                    className="text-white font-semibold py-4 px-8 rounded-full transition-all duration-300 ease-out hover:-translate-y-0.5 active:scale-[0.98] active:translate-y-0"
                    style={{ 
                      backgroundColor: '#1C2E4A',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#0F1E33';
                      e.target.style.boxShadow = '0 8px 16px rgba(0, 0, 0, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#1C2E4A';
                      e.target.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                    }}
                  >
                    Demander un diagnostic stratégique
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <a href="tel:0678269110">
                  <Button 
                    variant="outline" 
                    className="border-white text-white font-medium py-4 px-8 rounded-full transition-all duration-300 ease-out hover:bg-white/10 hover:-translate-y-0.5 active:scale-[0.98] active:translate-y-0"
                  >
                    <Phone className="mr-2 w-5 h-5" />
                    Être rappelé
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Création et optimisation WordPress */}
        <section className="py-16 md:py-24" style={{ backgroundColor: '#E9DFCF' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Globe className="w-6 h-6 text-blue-700" />
                  </div>
                  <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900" style={{ letterSpacing: '-0.04em' }}>
                    Création et optimisation WordPress
                  </h2>
                </div>
                <p className="text-lg text-gray-600 mb-8">
                  Votre site web est votre vitrine digitale. Nous créons des sites 
                  performants, esthétiques et optimisés pour convertir vos visiteurs en clients.
                </p>
                <div className="space-y-4">
                  {servicesWordpress.map((service, index) => (
                    <div key={index} className="bg-white p-4 rounded-xl border border-gray-200">
                      <h3 className="font-bold text-gray-900 mb-1">{service.title}</h3>
                      <p className="text-gray-600 text-sm">{service.description}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <img 
                  src="https://images.pexels.com/photos/14850053/pexels-photo-14850053.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" 
                  alt="Professionnel utilisant une tablette dans un bureau moderne"
                  className="rounded-2xl shadow-lg w-full"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Structuration digitale */}
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mb-12">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Code className="w-6 h-6 text-blue-700" />
                </div>
                <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900" style={{ letterSpacing: '-0.04em' }}>
                  Structuration digitale
                </h2>
              </div>
              <p className="text-lg text-gray-600">
                Organisez votre environnement numérique pour gagner en efficacité et en clarté.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {structuration.map((item, index) => (
                <div key={index} className="bg-gray-50 p-6 rounded-xl hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                    <item.icon className="w-6 h-6 text-blue-700" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Automatisation des flux */}
        <section className="py-16 md:py-24" style={{ backgroundColor: '#E9DFCF' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="bg-white rounded-2xl p-8 shadow-sm">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-blue-700 rounded-xl flex items-center justify-center">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Automatisations disponibles</h3>
                </div>
                <ul className="space-y-3">
                  {automatisation.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle className="w-3 h-3 text-green-600" />
                      </div>
                      <span className="text-gray-700 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-6" style={{ letterSpacing: '-0.04em' }}>
                  Automatisation des flux
                </h2>
                <p className="text-lg text-gray-600 mb-6">
                  Libérez-vous des tâches répétitives. Nous connectons vos outils 
                  pour créer des workflows automatisés qui travaillent pour vous 24h/24.
                </p>
                <p className="text-gray-600 mb-8">
                  Zapier, Make, n8n, Power Automate... nous maîtrisons les principales 
                  plateformes d'automatisation pour concevoir des solutions sur mesure.
                </p>
                <div className="bg-blue-50 rounded-xl p-6">
                  <p className="text-blue-800 font-medium">
                    <strong>Résultat moyen :</strong> 10 heures économisées par semaine 
                    grâce à l'automatisation des tâches administratives.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Référencement et visibilité */}
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mb-12">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Search className="w-6 h-6 text-blue-700" />
                </div>
                <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900" style={{ letterSpacing: '-0.04em' }}>
                  Référencement et visibilité
                </h2>
              </div>
              <p className="text-lg text-gray-600">
                Être visible sur Google est essentiel. Nous optimisons votre présence 
                en ligne pour attirer un trafic qualifié.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {seo.map((item, index) => (
                <div key={index} className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                  <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-24 bg-blue-700">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-6" style={{ letterSpacing: '-0.04em' }}>
              Développez votre présence digitale
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Demandez un diagnostic stratégique gratuit et personnalisé.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/#formulaire">
                <Button 
                  className="bg-white text-blue-700 font-semibold py-4 px-8 rounded-full shadow-lg transition-all duration-[250ms] ease-out hover:bg-gray-100 hover:-translate-y-0.5 hover:shadow-xl active:scale-[0.98] active:translate-y-0"
                >
                  Demander un diagnostic stratégique
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:0678269110">
                <Button 
                  variant="outline" 
                  className="border-white text-white font-medium py-4 px-8 rounded-full transition-all duration-[250ms] ease-out hover:bg-white/10 hover:-translate-y-0.5 active:scale-[0.98] active:translate-y-0"
                >
                  <Phone className="mr-2 w-5 h-5" />
                  Être rappelé
                </Button>
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default DigitalWebPage;
